
import { useState, useEffect } from 'react';
import { fetchBlogPosts } from './useBlogFetch';
import type { Database } from '@/integrations/supabase/types';

type BlogPost = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  author?: {
    first_name?: string;
    last_name?: string;
  } | null;
  tags?: { id: string; name: string }[];
  itineraries?: {
    id: string;
    title: string;
    destination?: string | null;
    slug?: string | null;
  }[];
};

interface UseBlogPostsParams {
  page?: number;
  limit?: number;
  tag?: string;
  category?: string;
}

export function usePublicBlogPosts({
  page = 1,
  limit = 9,
  tag,
  category
}: UseBlogPostsParams = {}) {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPosts, setTotalPosts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const loadPosts = async () => {
      console.log('[DEBUG] usePublicBlogPosts - Loading posts with params:', { page, limit, tag, category });
      setLoading(true);
      setError(null);

      try {
        const { data, count } = await fetchBlogPosts(page, limit, tag, category);
        
        console.log('[DEBUG] usePublicBlogPosts - Raw data received:', { dataLength: data.length, count });
        
        // Transform the posts to match our type
        const transformedPosts = data.map(post => {
          console.log('[DEBUG] usePublicBlogPosts - Processing post:', post.id, 'with tags:', post.tags);
          
          return {
            ...post,
            tags: post.tags?.map(tag => ({
              id: tag.tag_id.id,
              name: tag.tag_id.name
            })) || []
          };
        });
        
        console.log('[DEBUG] usePublicBlogPosts - Transformed posts:', transformedPosts.length);
        
        setPosts(transformedPosts);
        setTotalPosts(count);
        setTotalPages(Math.ceil(count / limit));
        setLoading(false);
      } catch (err) {
        console.error('[DEBUG] usePublicBlogPosts - Error fetching blog posts:', err);
        setError('Failed to load blog posts');
        setLoading(false);
      }
    };

    loadPosts();
  }, [page, limit, tag, category]);

  return { posts, loading, error, totalPosts, totalPages };
}
